// Whole-mountain natural snow-depth-by-altitude profile (Phase-2A).
//
// The single source of truth for "how much natural snow is really at THIS
// elevation", shared by the piste headline (calculatePisteDailyRepresentative)
// and the run-by-run scorer (trail-scoring.ts). v7 lapse-transports temperature,
// wind and precip-phase to any elevation for free, but snow_depth_m is the one
// input the lapse cannot move, so every band/run reads the summit's depth. This
// module corrects that.
//
//   Method A — instantaneous freezing-level-aware retention. Re-lights the v7
//     depth haircut by reconstructing the freezing level from the lapsed band
//     temperature when the archive returns null (~93% of rows). Per-hour,
//     per-elevation, zero new data. The graceful fallback when B1's trailing
//     window is unavailable (forecast days, the day-planner, thin coverage).
//
//   Method B1 — transport-differential. Trusts the stored high-band depth as the
//     one real anchor and models only the melt/accumulation differential between
//     a target elevation and that anchor over a trailing window, producing a
//     retention rho(elev) in [floor, 1] so depth(elev) = rho * highDepth (<= 1 by
//     construction, so the one-sided gate stance holds). Evaluated at any
//     elevation this is the continuous profile.
//
//   Method C — calibrated piste man-made / grooming modifier. Lifts the groomed
//     mid band's natural depth for snowmaking + nightly bashing. Season/cold
//     gated, honesty-bounded (a poor resort stays poor). Piste mid only.
//
// Open-Meteo snow_depth_m is physical depth in metres; everything here is metres.
// Copernicus CDS is validate-only (Phase 2B) and never feeds this module. See
// docs/superpowers/specs/2026-06-26-ski-representative-snow-depth-phase2-design.md.

import type { NormalisedHour } from "./daily-obs-types";
import { validateSnowDepthM } from "./snow-depth-validation";
// The piste band transform (temp lapse + precip-phase re-partition). Imported at
// runtime only; the piste-scoring <-> snow-depth-profile cycle is safe because
// neither side calls the other at module-init time.
import { lapseAdjustHour } from "./piste-scoring";

const LAPSE_RATE_C_PER_M = 0.0065;
const clampN = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/** The v7-calibrated freezing-level haircut span/floor (was piste-scoring.ts:1242-1243). */
export const DEPTH_HAIRCUT_SPAN_M = 800;
export const DEPTH_HAIRCUT_FLOOR = 0.15;

// ---------------------------------------------------------------------------
// Physical snow-depth ceiling — glacier-firn sentinel guard
// ---------------------------------------------------------------------------
//
// Reject invalid measurements before applying the legacy scoring ceiling. A
// glacier ice-column reading is unknown seasonal cover, never six metres of snow.
// The six-metre ceiling remains for valid deep seasonal measurements to preserve
// the established profile calculation; it is not a source-validation substitute.
export const MAX_PHYSICAL_SNOW_DEPTH_M = 6;

// The interp gradient (band_depths) is only TRUSTED when the Open-Meteo archive
// actually resolved depth by elevation. For ~68% of resorts the ERA5 archive grid
// is too coarse to differentiate, so it returns the SAME depth at base/mid/summit
// (a degenerate flat carry — the very thing the gradient was meant to fix), and on
// glacier cells it returns the ~22-33 m firn. In both cases the honest move is to
// fall back to Method-A/B1 — "trust the one real reading at its known elevation and
// compute the gradient around it from the lapse/freezing-level." `bandGradientUsable`
// is the gate: a real, physical base→summit spread engages interp; flat/firn falls
// back. See docs/audits/2026-06-27-per-run-scoring-step5-findings.md.
/** Above this a band depth is glacier firn, not a skiable base (real snow ≤ ~12 m). */
export const GLACIER_FIRN_MIN_M = 15;
/** Minimum base→summit depth spread (m) for the archive gradient to be trusted. */
export const GRADIENT_MIN_SPREAD_M = 0.1;

/**
 * Validate seasonal cover, then cap valid deep snow for the legacy profile.
 * Missing, negative, non-finite and glacier-firn values remain unavailable.
 */
export function clampPhysicalSnowDepthM(depthM: number | null | undefined): number | null {
  const valid = validateSnowDepthM(depthM).valueM;
  return valid === null ? null : Math.min(valid, MAX_PHYSICAL_SNOW_DEPTH_M);
}

/**
 * The hour with its snow_depth_m clamped to the physical ceiling. Returns the
 * SAME object reference when no clamp is needed, so a resort whose depths are all
 * physical is byte-identical and existing scoring is provably unaffected.
 */
export function clampHourSnowDepth(h: NormalisedHour): NormalisedHour {
  const d = h.weather.snow_depth_m;
  const validated = clampPhysicalSnowDepthM(d);
  if (d === validated) return h;
  return { ...h, weather: { ...h.weather, snow_depth_m: validated } };
}

/**
 * Map a band's hours through clampHourSnowDepth. Returns the SAME array reference
 * when every hour is already physical (identity for normal resorts).
 */
export function clampHoursSnowDepth(hours: NormalisedHour[]): NormalisedHour[] {
  let changed = false;
  const out = hours.map((h) => {
    const c = clampHourSnowDepth(h);
    if (c !== h) changed = true;
    return c;
  });
  return changed ? out : hours;
}

/**
 * Freezing level reconstructed from a band's (lapsed) temperature, used when the
 * archive returns null for freezing_level_m. The freezing (0 °C) line sits
 * T/0.0065 metres above a band at temperature T.
 */
export function reconstructFreezingLevelM(bandTempC: number, bandElevM: number): number {
  return bandElevM + bandTempC / LAPSE_RATE_C_PER_M;
}

/**
 * Method A — instantaneous freezing-level-aware depth retention for a band.
 * Prefers the real freezing level; reconstructs it from the band temperature
 * when null. Identical to the v7 haircut when a real FL is present, and
 * algebraically clamp(1 - T_band/(span*0.0065), floor, 1) when reconstructed:
 * inert at T <= 0, floors at T ~= span*0.0065 °C. Per-hour, per-elevation, zero
 * new data.
 */
export function methodADepthRetention(
  realFreezingLevelM: number | null | undefined,
  bandTempC: number,
  bandElevM: number,
  spanM: number,
  floor: number,
): number {
  const fl = realFreezingLevelM ?? reconstructFreezingLevelM(bandTempC, bandElevM);
  const above = fl - bandElevM;
  if (above <= 0) return 1;
  return clampN(1 - above / spanM, floor, 1);
}

// ---------------------------------------------------------------------------
// Method B1 — transport-differential continuous profile
// ---------------------------------------------------------------------------

/**
 * The trusted high anchor and the day's anchor depth.
 *   dataElevM   — elevation the stored hourly was fetched at (lapse origin).
 *   anchorElevM — the high anchor for the temperature differential (resort top);
 *                 depth is trusted at/above here, so rho <= 1 keeps the summit raw.
 *   highDepthM  — the day's trusted high-band physical depth (metres).
 */
export type DepthAnchors = {
  dataElevM: number;
  anchorElevM: number;
  highDepthM: number;
};

export type B1Params = {
  /** cm of physical depth melted per +1 °C·hour the target runs warmer than the anchor. */
  ddfCmPerDegC: number;
  /** cm of depth lost per mm of rain-on-snow at the target. */
  rainOnSnowCmPerMm: number;
  /** cm of accumulated deficit healed per cm of fresh snow at the target. */
  replenishPerCm: number;
  /** retention floor (a melted-out band never reads as totally bare while the anchor holds). */
  floor: number;
  /** trailing window length the model integrates over (days). */
  windowDays: number;
};

/**
 * B1 constants CALIBRATED against the Open-Meteo depth-vs-elevation profile
 * (scripts/v8-depth-calibrate.ts, 2026-06-26). The fit was run over 20 resorts ×
 * a full winter, restricted to gradient-bearing resorts after filtering glacier
 * sentinels (~33 m) and stepped/flat-profile rows. The MAE surface is flat across
 * ddf 0.02-0.05 / window 5-10 (the model gradient is gentle), so these sit in the
 * fitted optimum (gradient-bearing depth MAE ~0.36 m vs ~0.46 m at the first
 * guess). Phase 2B (CDS ERA5) perfects them against the gold-standard truth.
 */
export const DEFAULT_B1_PARAMS: B1Params = {
  // ERA5-validated value (Phase-2B CDS drift-monitor calibration, 2026-06-26).
  ddfCmPerDegC: 0.028,
  rainOnSnowCmPerMm: 0.4,
  replenishPerCm: 1.0,
  floor: 0.1,
  windowDays: 7,
};

const EPS_CM = 1;

/**
 * Method B1 retention at a target elevation. Trusts the stored high-band depth as
 * the one real anchor and integrates a degree-day melt DEFICIT (extra melt where
 * the target runs warmer than the anchor, plus rain-on-snow, minus fresh-snow
 * replenishment) over a trailing window, normalised by the anchor depth:
 *   rho = clamp(1 - deficit_cm / (highDepth_cm + EPS), floor, 1)
 * Anchored to the model's own snowpack (never invents an absolute base),
 * cumulative/stateful, monotone in elevation, and thin-pack sensitive by
 * construction. windowDays is day-ordered oldest→newest with the scored day last.
 */
export function naturalRetentionB1(
  targetElevM: number,
  windowDays: NormalisedHour[][],
  anchors: DepthAnchors,
  params: B1Params,
): number {
  const win = windowDays.slice(-params.windowDays);
  const dTarget = targetElevM - anchors.dataElevM;
  const dAnchor = anchors.anchorElevM - anchors.dataElevM;
  const highDepthCm = anchors.highDepthM * 100;

  let deficitCm = 0;
  for (const day of win) {
    for (const h of day) {
      const tH = lapseAdjustHour(h, dTarget).weather;
      const aH = lapseAdjustHour(h, dAnchor).weather;
      // Extra melt where the target is warmer than the anchor (both clamped at 0 °C).
      deficitCm += params.ddfCmPerDegC * Math.max(0, Math.max(0, tH.temp_c) - Math.max(0, aH.temp_c));
      // The anchor already includes its own rain losses. Transport only the
      // extra liquid precipitation at the target, including convective showers.
      const targetRain = (tH.rain_mm ?? 0) + (tH.showers_mm ?? 0);
      const anchorRain = (aH.rain_mm ?? 0) + (aH.showers_mm ?? 0);
      deficitCm += params.rainOnSnowCmPerMm * Math.max(0, targetRain - anchorRain);
      // Fresh snow at the target re-covers the divergence (heals the cumulative deficit).
      deficitCm = Math.max(0, deficitCm - params.replenishPerCm * (tH.snowfall_cm ?? 0));
    }
  }
  return clampN(1 - deficitCm / (highDepthCm + EPS_CM), params.floor, 1);
}

export type NaturalDepthResult = { depthM: number; retention: number; method: "A" | "B1" | "interp" };

export type NaturalDepthOpts = {
  /** Trailing window (day-ordered, scored day last). >=2 days engages B1. */
  window?: NormalisedHour[][];
  /** The scored day's hours (at the data elevation) — the Method A fallback input. */
  dayHours: NormalisedHour[];
  /** B1 constants; required to engage B1. */
  b1?: B1Params;
  haircutSpanM: number;
  haircutFloor: number;
  /**
   * Phase-3 multi-anchor band depths. When present and ≥2 cleaned points are
   * available, the interpolation path is used as the PRIMARY natural-depth
   * estimate (method:"interp"); B1/A become the fallback.
   */
  bandAnchors?: BandAnchors;
};

/**
 * The continuous natural depth profile at any elevation.
 *
 * PRIMARY path (Phase-3): when `opts.bandAnchors` is present and has ≥2
 * cleaned anchor points, piecewise-linear interpolation between the observed
 * band depths is used (method:"interp"). The haircut/Method-C modifiers are
 * applied by callers as today.
 *
 * FALLBACK: a trailing window (>=2 days) engages Method B1; otherwise Method A
 * (the mean per-hour reconstructed-FL retention over the day, lapsed to the
 * target). Returns physical depth in metres = retention * highDepth.
 */
export function naturalDepthAtElevation(
  targetElevM: number,
  anchors: DepthAnchors,
  opts: NaturalDepthOpts,
): NaturalDepthResult {
  // Phase-3 primary path: interp ONLY when the band_depths resolved a real,
  // physical per-elevation gradient. Flat (archive didn't resolve elevation) or
  // firn-contaminated band_depths fall through to Method-A/B1 below.
  if (bandGradientUsable(opts.bandAnchors)) {
    const depthM = interpolateNaturalDepth(targetElevM, opts.bandAnchors!);
    // Retention is informational (depthM relative to the high anchor).
    const retention =
      anchors.highDepthM > 0 ? depthM / anchors.highDepthM : depthM > 0 ? 1 : 0;
    return { depthM, retention, method: "interp" };
  }

  const dTarget = targetElevM - anchors.dataElevM;
  // Method A retention (mean per-hour reconstructed-FL haircut over the day).
  const retsA = opts.dayHours.map((h) => {
    const w = lapseAdjustHour(h, dTarget).weather;
    const anchorWeather = lapseAdjustHour(h, anchors.anchorElevM - anchors.dataElevM).weather;
    const targetAbove = Math.max(0, (w.freezing_level_m ?? reconstructFreezingLevelM(w.temp_c, targetElevM)) - targetElevM);
    const anchorAbove = Math.max(0, (anchorWeather.freezing_level_m ?? reconstructFreezingLevelM(anchorWeather.temp_c, anchors.anchorElevM)) - anchors.anchorElevM);
    // A supplied depth remains the anchor even during a thaw. This is a
    // relative altitude proxy, not a second instantaneous melt calculation.
    return clampN(1 - (targetAbove - anchorAbove) / opts.haircutSpanM, opts.haircutFloor, 1);
  });
  const retA = retsA.length ? retsA.reduce((a, b) => a + b, 0) / retsA.length : 1;

  if (opts.window && opts.window.length >= 2 && opts.b1) {
    const retB1 = naturalRetentionB1(targetElevM, opts.window, anchors, opts.b1);
    // Method A is the instantaneous deflation FLOOR: B1 adds cumulative melt
    // memory and may deflate FURTHER, but must never read DEEPER than the
    // warm-now haircut (so a sustained thaw can never raise the score above A).
    const retention = Math.min(retB1, retA);
    return { depthM: retention * anchors.highDepthM, retention, method: "B1" };
  }
  return { depthM: retA * anchors.highDepthM, retention: retA, method: "A" };
}

/**
 * Sanity cross-check: the absolute difference (metres) between the interp
 * primary path and the B1 fallback at `elevM`. Returns `null` unless BOTH
 * paths are computable — i.e. `opts.bandAnchors` has ≥2 cleaned points AND
 * `opts.window?.length >= 2 && opts.b1` is set. Used by the logged drift
 * monitor to flag large profile disagreements.
 */
export function b1DisagreementM(
  elevM: number,
  anchors: DepthAnchors,
  opts: NaturalDepthOpts,
): number | null {
  // Interp side: need ≥2 cleaned anchor points.
  const cleanedPts = cleanDepthAnchors(opts.bandAnchors?.points ?? []);
  if (cleanedPts.length < 2) return null;
  // B1 side: need a window of ≥2 days and B1 params.
  if (!opts.window || opts.window.length < 2 || !opts.b1) return null;

  const interp = interpolateNaturalDepth(elevM, opts.bandAnchors!);
  // Reuse the function with bandAnchors stripped so the B1 profile is exactly
  // what the fallback yields (not recursing into the interp branch).
  const b1Depth = naturalDepthAtElevation(elevM, anchors, { ...opts, bandAnchors: undefined }).depthM;
  return Math.abs(interp - b1Depth);
}

// ---------------------------------------------------------------------------
// Phase-3 multi-anchor depth interpolation core
// ---------------------------------------------------------------------------

/**
 * One or more (elevation, depth) anchor pairs — any order, any number ≥ 1.
 * Typically the native cell plus base / mid / summit band points.
 */
export type BandAnchors = {
  points: { elevM: number; depthM: number; source?: "band" | "native" }[];
  /** Daily maxima cannot establish the depth of an earlier individual hour. */
  dailyMaximum?: boolean;
  hourly?: { hour: number; points: BandAnchors["points"] }[];
};

/** Select only contemporaneous band evidence. Never fill a gap from later hours. */
export function bandAnchorsAtHour(anchors: BandAnchors | undefined, hour: number): BandAnchors | undefined {
  if (!anchors) return undefined;
  if (anchors.hourly) {
    const matches = anchors.hourly.filter(point => point.hour === hour);
    return matches.length === 1 ? { points: matches[0].points } : undefined;
  }
  return anchors.dailyMaximum ? undefined : anchors;
}

/**
 * Sort without inventing deeper snow at higher elevations. Credible inversions
 * can result from wind and terrain. Duplicate elevations use their mean depth;
 * an explicit band takes precedence over a coarse native-cell estimate.
 * Does NOT mutate the input array — returns a fresh array.
 */
export function cleanDepthAnchors(
  points: { elevM: number; depthM: number; source?: "band" | "native" }[],
): { elevM: number; depthM: number; source?: "band" | "native" }[] {
  const groups = new Map<number, typeof points>();
  for (const point of points) {
    if (!Number.isFinite(point.elevM) || validateSnowDepthM(point.depthM).valueM === null) continue;
    groups.set(point.elevM, [...(groups.get(point.elevM) ?? []), point]);
  }
  return [...groups].sort(([left], [right]) => left - right).map(([elevM, group]) => {
    const bands = group.filter(point => point.source !== "native");
    const selected = bands.length ? bands : group;
    return { ...selected[0], elevM, depthM: selected.reduce((sum, p) => sum + p.depthM, 0) / selected.length };
  });
}

/**
 * Piecewise-linear depth at any target elevation from a set of cleaned or
 * un-cleaned anchors. Anchors are sorted and deduplicated internally.
 *   • Single anchor → returns that depth everywhere.
 *   • Target below the lowest anchor → lowest depth (clamp, no extrapolation).
 *   • Target above the highest anchor → highest depth (clamp, no extrapolation).
 *   • Otherwise → linear interpolation between the bracketing pair.
 */
export function interpolateNaturalDepth(
  targetElevM: number,
  anchors: BandAnchors,
): number {
  const pts = cleanDepthAnchors(anchors.points);
  if (!pts.length) throw new Error("Natural depth interpolation requires a valid anchor");
  if (pts.length === 1) return pts[0].depthM;
  if (targetElevM <= pts[0].elevM) return pts[0].depthM;
  if (targetElevM >= pts[pts.length - 1].elevM) return pts[pts.length - 1].depthM;
  // Find the bracketing pair
  for (let i = 0; i < pts.length - 1; i++) {
    const lo = pts[i];
    const hi = pts[i + 1];
    if (targetElevM >= lo.elevM && targetElevM <= hi.elevM) {
      const t = (targetElevM - lo.elevM) / (hi.elevM - lo.elevM);
      return lo.depthM + t * (hi.depthM - lo.depthM);
    }
  }
  // Should never be reached given the clamps above
  return pts[pts.length - 1].depthM;
}

/**
 * Whether the stored `band_depths` actually resolved a real per-elevation gradient
 * and can be trusted as the interp profile. Returns FALSE — so callers fall back to
 * Method-A/B1 (the lapse + freezing-level gradient) — when:
 *   • there are < 2 cleaned anchor points (nothing to interpolate), OR
 *   • the highest cleaned anchor is glacier firn (> GLACIER_FIRN_MIN_M), so the
 *     archive returned ice-column depth at some band, OR
 *   • the base→summit spread is below GRADIENT_MIN_SPREAD_M — the ERA5 archive grid
 *     could not resolve elevation and handed back the same depth at every band
 *     (a degenerate flat carry).
 * Only a genuine, physical gradient engages interp.
 */
export function bandGradientUsable(anchors: BandAnchors | undefined | null): boolean {
  if (!anchors) return false;
  const bands = anchors.points.filter((p) => p.source !== "native");
  if (bands.some(p => !Number.isFinite(p.elevM) || validateSnowDepthM(p.depthM).valueM === null)) return false;
  const pts = cleanDepthAnchors(bands);
  if (pts.length < 2) return false;
  const min = Math.min(...pts.map(point => point.depthM));
  const max = Math.max(...pts.map(point => point.depthM));
  if (max >= GLACIER_FIRN_MIN_M) return false; // firn-contaminated band
  if (max - min < GRADIENT_MIN_SPREAD_M) return false; // flat — archive didn't resolve elevation
  return true;
}

/** Convenience: the profile evaluated at several elevations (the multi-altitude harness). */
export function depthProfile(
  targetElevsM: number[],
  anchors: DepthAnchors,
  opts: NaturalDepthOpts,
): Map<number, NaturalDepthResult> {
  const out = new Map<number, NaturalDepthResult>();
  for (const e of targetElevsM) out.set(e, naturalDepthAtElevation(e, anchors, opts));
  return out;
}

// ---------------------------------------------------------------------------
// Method C: historical unvalidated managed-depth experiment
// ---------------------------------------------------------------------------

/**
 * Is `mmdd` (a "MM-DD" string) inside the resort's typical season? Handles the
 * year-wrap (start > end, e.g. a northern "12-01".."04-30"). Null bounds default
 * to in-season. The single source of truth for the inline pattern that was
 * duplicated across timing-data.ts and the measurement scripts.
 */
export function isInSkiSeason(mmdd: string, start: string | null, end: string | null): boolean {
  if (!start || !end) return true;
  return start <= end ? mmdd >= start && mmdd <= end : mmdd >= start || mmdd <= end;
}

/**
 * Provisional Method C constants — CALIBRATED in scripts/v8-depth-calibrate.ts by
 * regressing artificial-surface reported depth on B1 natural depth. Honesty-bounded
 * so a poor resort stays poor (plan Task 7).
 */
export const DEFAULT_METHOD_C = {
  /** grooming/compaction multiplier on the natural pack. */
  multiplier: 1.15,
  /** snowmaking additive floor (metres) on a thin natural pack. */
  additiveM: 0.1,
  /** the season-guaranteed minimum operable base (metres) when cold enough. */
  snowmakingFloorM: 0.25,
} as const;

/**
 * The honesty cap: Method C's man-made FABRICATION may not push the depth gate
 * out of the "open but thin/poor" region. snowDepthGate keys on depth/adequate;
 * sigmoid(6·(ratio−0.5)) stays well below 0.5 for ratio ≤ ~0.3, so the fabricated
 * floor is capped at this fraction of the resort's adequate base. Keeps a
 * small-adequate-base resort (Perisher 30 cm) poor; a no-op on normal resorts
 * (0.3·100 = 30 cm > the 25 cm floor) and on real deep snow (the multiplier path).
 */
const METHOD_C_POOR_RATIO = 0.3;

/**
 * The groomed mid band can make/hold man-made snow when its daytime mean
 * temperature is at or below this (°C). A loose "operable" threshold, not the
 * tighter wet-bulb snowmaking limit — CALIBRATED in Task 7. Above it, Method C
 * is inert so a spring thaw never fabricates an operating base.
 */
export const SNOWMAKING_HOLD_TEMP_C = 2;
const METHOD_C_FULL_HOLD_TEMP_C = SNOWMAKING_HOLD_TEMP_C - 1;
const METHOD_C_ZERO_HOLD_TEMP_C = SNOWMAKING_HOLD_TEMP_C + 1;

export function methodCHoldFactor(meanDaytimeTempC: number): number {
  if (meanDaytimeTempC <= METHOD_C_FULL_HOLD_TEMP_C) return 1;
  if (meanDaytimeTempC >= METHOD_C_ZERO_HOLD_TEMP_C) return 0;
  return (METHOD_C_ZERO_HOLD_TEMP_C - meanDaytimeTempC) /
    (METHOD_C_ZERO_HOLD_TEMP_C - METHOD_C_FULL_HOLD_TEMP_C);
}

export type MethodCConfig = {
  typicalSeasonStart: string | null;
  typicalSeasonEnd: string | null;
  /** The day can make/hold snow on the groomed mid band (caller-derived from band temp). */
  coldEnough: boolean;
  /** Optional smoothed hold factor. Omitted preserves the legacy binary coldEnough gate. */
  holdFactor?: number;
};

/**
 * Groomed-piste operating depth: natural B1/A depth lifted by the operating pack
 * (snowmaking + nightly bashing), the one place depth is adjusted UP. Season/cold
 * gated — never fabricates a summer or warm-day base. Honesty-bounded: the
 * The caller first proves base adequacy through C0.2-P. This lower-level
 * function then applies the season and temperature persistence mechanics.
 * The snowmaking floor is calibrated so a genuinely poor resort (Perisher ~5 cm)
 * reads open-but-thin under snowDepthGate, never green. Off-piste, touring and
 * the piste SUMMIT band do NOT get this — natural only.
 */
/** @deprecated Historical experiment only. No current scorer imports this uplift. */
export function pisteOperatingDepth(
  naturalDepthM: number,
  mmdd: string,
  config: MethodCConfig,
  adequateBaseCm: number,
  params: { multiplier: number; additiveM: number; snowmakingFloorM: number } = DEFAULT_METHOD_C,
): number {
  const holdFactor = clampN(config.holdFactor ?? (config.coldEnough ? 1 : 0), 0, 1);
  if (holdFactor <= 0) return naturalDepthM;
  if (!isInSkiSeason(mmdd, config.typicalSeasonStart, config.typicalSeasonEnd)) return naturalDepthM;
  const raw = Math.max(
    naturalDepthM * params.multiplier,
    naturalDepthM + params.additiveM,
    params.snowmakingFloorM,
  );
  // Honesty bound: the grooming multiplier on REAL natural snow is always allowed
  // under this legacy assumption, which is not a physical law. The additive uplift
  // is capped at METHOD_C_POOR_RATIO of the resort's adequate base, so a thin
  // man-made base reads "open but poor" for ANY resort scale, never green.
  const fabricationCapM = Math.max(naturalDepthM * params.multiplier, (METHOD_C_POOR_RATIO * adequateBaseCm) / 100);
  const operatingDepthM = Math.min(raw, fabricationCapM);
  return naturalDepthM + (operatingDepthM - naturalDepthM) * holdFactor;
}
